"use client";

import BulkImportArticle from "../../../components/BulkImportArticle";

const data = {
  title: "Bulk Import — Customers & Vendors",
  audience: "For: Anyone adding many customers or vendors at once",
  intro: [
    "Upload many customers or vendors at once from an Excel/CSV-style sheet instead of creating them one by one. This page explains what each row needs and what will cause a row to be rejected.",
  ],
  steps: {
    commitVerb: "Create the records",
    commitLabel: "Create",
    allOrNothing:
      "Creating is all-or-nothing: if even one row has a problem, none of the customers/vendors are created — the whole batch is rejected and rolled back. This is why checking first is strongly recommended.",
  },
  calloutsAfterSteps: [
    {
      type: "danger",
      text: "Always mark each row explicitly as Customer or Vendor. A row left blank passes the Check step but is silently created as a Vendor on the final step — mark exactly one role to avoid surprises.",
    },
  ],
  requiredFields: [
    { field: "Display Name", needs: "A unique name. It can't repeat elsewhere in the same sheet, and can't already exist for the same role (see Duplicates below)." },
    { field: "Company Name", needs: "Required on every row." },
    { field: "Primary Contact First Name", needs: "Required on every row." },
    { field: "Business Partner Type", needs: "Exactly 'Business' or 'Individual'." },
    { field: "GST Treatment", needs: "Required on every row." },
    { field: "Currency", needs: "A currency code (e.g. INR, USD) already configured for your company — it isn't created from the sheet." },
    { field: "Role (Customer / Vendor)", needs: "Mark each row as Customer or Vendor. Don't leave it blank (see the warning above)." },
  ],
  conditionalFields: [
    { field: "Email", rule: "Must be a valid email address format, e.g. name@company.com." },
    { field: "Phone Number", rule: "Digits only — no +, spaces, or dashes." },
    { field: "GSTIN/UIN", rule: "Normally optional, but becomes required if GST Treatment is 'Registered Business Regular'." },
    { field: "Tax Preference", rule: "Must be 'Taxable' or 'Tax Exempt'." },
    { field: "Payment Terms", rule: "One of: Due On Receipt, Due end of the month, Due end of next month, Net 15, Net 30, Net 45, Net 60, Net 75, Advance, After Dispatch." },
  ],
  matchFields: [
    { field: "Currency", rule: "Must be a currency code already set up for your company." },
    { field: "Billing / Shipping Country", rule: "Must match a real country name in the system. Country can stand alone." },
    { field: "Billing / Shipping State", rule: "If a State is entered, both a City and a Country are required, and the State must belong to that Country." },
    { field: "Billing / Shipping City", rule: "If a City is entered, a State is required (and via the State, a Country). The City must belong to that State." },
  ],
  matchNote:
    "Addresses follow a Country → State → City hierarchy, applied separately to Billing and Shipping. You can leave an address blank, but if you fill in part of it, the level above must also be filled in. Country, State, and City must each match real names already in the system.",
  duplicateRules: [
    {
      condition: "'Rahul Traders' exists as a Vendor, and you import 'Rahul Traders' as a Customer",
      result: "Allowed — the same name can exist once as a customer and once as a vendor; roles are tracked separately.",
    },
    {
      condition: "'Rahul Traders' exists as a Customer, and you import another 'Rahul Traders' as a Customer",
      result: "Rejected — it already exists in that same role.",
    },
    {
      condition: "The same Display Name is used twice in your sheet",
      result: "Rejected — names must be unique within the upload (not case-sensitive).",
    },
  ],
  mistakes: [
    { problem: "Display Name missing, or used twice in the sheet", meaning: "Every row needs a name, unique within the upload." },
    { problem: "Display Name already used before in the same role", meaning: "That customer (or vendor) already exists — use a different name, or the other role." },
    { problem: "Business Partner Type blank or misspelled", meaning: "Must be exactly 'Business' or 'Individual'." },
    { problem: "Phone includes +, spaces, or dashes", meaning: "Enter digits only." },
    { problem: "GST Treatment is 'Registered Business Regular' but GSTIN/UIN is missing", meaning: "GSTIN/UIN becomes mandatory the moment that treatment is selected." },
    { problem: "Currency code missing or not set up for the company", meaning: "Only currencies already configured for your company can be used." },
    { problem: "City filled but State blank (or State filled but City/Country blank)", meaning: "Follow the Country → State → City hierarchy." },
    { problem: "Country / State / City name doesn't match", meaning: "Must match existing names; State must belong to the Country and City to the State." },
  ],
  onApply: [
    "Each record is created with a sequential customer/vendor number (using your company's numbering).",
    "The role you marked decides the account used (receivable for customers, payable for vendors) and the number prefix.",
    "Billing and shipping addresses are attached as entered, matched to the country/state/city records.",
    "The whole batch is written together in a single transaction — if anything fails, nothing is saved.",
  ],
  workflow: [
    "Build your sheet with all the customers/vendors you want to add.",
    "Mark every row explicitly as Customer or Vendor.",
    "Run the Check step first and review the list of problem rows.",
    "Fix each issue in the sheet using the tables above as a guide.",
    "Run the Check step again until every row passes, then run Create.",
    "Tip: if you edit the sheet after checking, run Check once more before creating — new mistakes can slip in.",
  ],
};

export default function BulkCustomerImportPage() {
  return <BulkImportArticle data={data} />;
}
